import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Input,
  Select,
  Switch,
  FormControl,
  FormLabel,
  Button,
  Badge,
  Link,
  SimpleGrid,
  RadioGroup,
  Radio,
  Stack,
  Spinner,
  Alert,
  AlertIcon,
  IconButton,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaArrowUp, FaArrowDown, FaTrash } from "react-icons/fa";
import { cqAPI, getBid } from "../../lib/Auth";
import {
  ivApi,
  isOk,
  isPermissionRefused,
  validateQuestions,
  EXAMPLE_QUESTIONS,
  MAX_QUESTIONS,
  MAX_QUESTION_CHARS,
  PORTAL_STATES,
  SETUP_PATH,
} from "./insuranceVerification";

/**
 * Settings › Insurance verification (SS-3944, practice admin): practice details, insurer
 * names, insurance portals, the PDF to the PMS and the practice's insurance questions.
 * Each section saves on its own; the guided setup reuses the same sections.
 */

export const useIvColors = () => ({
  cardBg: useColorModeValue("#FFFFFF", "#212330"),
  labelColor: useColorModeValue("#11131B", "#F9F9FB"),
  subColor: useColorModeValue("#64728C", "#A9B1C2"),
  dividerColor: useColorModeValue("#E5E5EF", "#3A3D4D"),
  primary: "#154043",
});

export function SectionCard({ title, subtitle, children, testId }) {
  const c = useIvColors();
  return (
    <Box bg={c.cardBg} p={{ base: 4, md: 6 }} borderRadius="16px" boxShadow="0 1px 4px rgba(0,0,0,0.06)" mb="5" data-testid={testId}>
      <Text fontSize="16px" fontWeight="700" color={c.labelColor}>{title}</Text>
      {subtitle && <Text fontSize="13px" color={c.subColor} mb="4">{subtitle}</Text>}
      {children}
    </Box>
  );
}

const SaveRow = ({ onSave, saving, message, error, label = "Save" }) => (
  <Flex align="center" gap="3" mt="4">
    <Button size="sm" bg="#154043" color="#F9F9FB" _hover={{ bg: "#123536" }} borderRadius="8px" onClick={onSave} isLoading={saving}>{label}</Button>
    {message && <Text fontSize="13px" color="#1F7A4D" data-testid="iv-saved">{message}</Text>}
    {error && <Text fontSize="13px" color="#B3261E" data-testid="iv-save-error">{error}</Text>}
  </Flex>
);

const MISSING_LABELS = {
  "office.legal_name": "legal office name",
  "office.tax_id": "tax id (9 digits)",
  "office.organization_npi": "practice NPI (10 digits)",
  "office.address": "address",
};

// ── practice details ─────────────────────────────────────────────────────────

const nameParts = (name) => {
  const [first = "", ...rest] = String(name || "").trim().split(/\s+/);
  return { first_name: first, last_name: rest.join(" ") };
};

export function PracticeDetails({ settings, onSaved, saveLabel }) {
  const office = settings?.office || {};
  const [form, setForm] = useState({
    legal_name: office.legal_name || "",
    address1: office.address?.address1 || "",
    address2: office.address?.address2 || "",
    city: office.address?.city || "",
    state: office.address?.state || "",
    zip: office.address?.zip || "",
    tax_id: office.tax_id || "",
    organization_npi: office.organization_npi || "",
  });
  const [npis, setNpis] = useState(settings?.provider_npis || []);
  const [defaultProvider, setDefaultProvider] = useState(settings?.default_provider_id || "");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    cqAPI.get("/providers").then((r) => {
      if (!live || !r || r.type !== "ok") return;
      setNpis((cur) => {
        const known = new Set(cur.map((p) => String(p.provider_id)));
        // keyed on the PMS provider id when there is one: prefill and the worklist return it for PMS appointments
        const idOf = (p) => String(p.pms_provider_id || p.id);
        const added = (r.data || []).filter((p) => !known.has(idOf(p)) && !known.has(String(p.id)))
          .map((p) => ({ provider_id: idOf(p), ...nameParts(p.name), npi: "" }));
        return [...cur, ...added];
      });
    });
    return () => { live = false; };
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setNpi = (i, k) => (e) => setNpis((list) => list.map((p, j) => (j === i ? { ...p, [k]: e.target.value } : p)));

  const save = async () => {
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const body = {
        office: {
          legal_name: form.legal_name.trim(),
          address: { address1: form.address1.trim(), address2: form.address2.trim(), city: form.city.trim(), state: form.state.trim(), zip: form.zip.trim() },
          tax_id: form.tax_id.trim(),
          organization_npi: form.organization_npi.trim(),
        },
        provider_npis: npis.filter((p) => String(p.provider_id || "").trim()).map((p) => ({ provider_id: String(p.provider_id).trim(), first_name: p.first_name || "", last_name: p.last_name || "", npi: String(p.npi || "").trim() })),
        default_provider_id: defaultProvider,
      };
      const r = await ivApi.putSettings(body);
      if (isOk(r)) {
        setMessage(r.warning || "Saved");
        onSaved && onSaved(r.data);
      } else {
        setError([r?.msg || "Could not save", ...(r?.missing || []).map((m) => MISSING_LABELS[m] || m)].join(" — "));
      }
    } finally {
      setSaving(false);
    }
  };

  const input = (k, label, props = {}) => (
    <FormControl>
      <FormLabel fontSize="13px" mb="1">{label}</FormLabel>
      <Input size="sm" borderRadius="8px" value={form[k]} onChange={set(k)} data-testid={`iv-office-${k}`} {...props} />
    </FormControl>
  );

  return (
    <SectionCard title="Practice details" subtitle="Exactly as registered with insurers." testId="iv-practice-details">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
        {input("legal_name", "Legal office name")}
        {input("tax_id", "Tax ID")}
        {input("address1", "Address")}
        {input("address2", "Address line 2")}
        {input("city", "City")}
        <SimpleGrid columns={2} spacing={3}>
          {input("state", "State")}
          {input("zip", "ZIP")}
        </SimpleGrid>
        {input("organization_npi", "Practice NPI")}
      </SimpleGrid>
      <Text fontSize="13px" fontWeight="700" mt="5" mb="2">Dentists</Text>
      {npis.map((p, i) => (
        <SimpleGrid key={`row-${i}`} columns={{ base: 1, md: p.manual ? 4 : 3 }} spacing={2} mb="2" alignItems="end" data-testid="iv-dentist-row">
          {p.manual && (
            <Input size="sm" borderRadius="8px" value={p.provider_id || ""} placeholder="PMS provider ID" onChange={setNpi(i, "provider_id")} data-testid="iv-dentist-provider-id" />
          )}
          <Input size="sm" borderRadius="8px" value={p.first_name || ""} placeholder="First name" onChange={setNpi(i, "first_name")} />
          <Input size="sm" borderRadius="8px" value={p.last_name || ""} placeholder="Last name" onChange={setNpi(i, "last_name")} />
          <Input size="sm" borderRadius="8px" value={p.npi || ""} placeholder="NPI" onChange={setNpi(i, "npi")} data-testid="iv-dentist-npi" />
        </SimpleGrid>
      ))}
      <Button size="xs" variant="ghost" color="#8E4997" onClick={() => setNpis((list) => [...list, { provider_id: "", first_name: "", last_name: "", npi: "", manual: true }])}
        data-testid="iv-dentist-add">
        + Add a dentist from your PMS (by provider ID)
      </Button>
      <FormControl mt="2" maxW="320px">
        <FormLabel fontSize="13px" mb="1">Default dentist for checks</FormLabel>
        <Select size="sm" borderRadius="8px" value={defaultProvider} onChange={(e) => setDefaultProvider(e.target.value)} placeholder="None">
          {npis.filter((p) => p.provider_id).map((p) => <option key={p.provider_id} value={p.provider_id}>{[p.first_name, p.last_name].filter(Boolean).join(" ") || p.provider_id}</option>)}
        </Select>
      </FormControl>
      <SaveRow onSave={save} saving={saving} message={message} error={error} label={saveLabel} />
    </SectionCard>
  );
}

// ── payer search (shared) ────────────────────────────────────────────────────

export function PayerSearch({ onPick, placeholder = "Search insurers", buttonLabel = "Search" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const search = async () => {
    const r = await ivApi.searchPayers(q);
    setResults(isOk(r) ? r.data || [] : []);
  };
  return (
    <Box>
      <Flex gap="2">
        <Input size="sm" borderRadius="8px" value={q} placeholder={placeholder} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") search(); }} data-testid="iv-payer-search-input" />
        <Button size="sm" variant="outline" onClick={search} data-testid="iv-payer-search-button">{buttonLabel}</Button>
      </Flex>
      {results && (
        <Box mt="1" maxH="180px" overflowY="auto" border="1px solid #E5E5EF" borderRadius="8px">
          {results.length === 0 && <Text fontSize="12px" p="2" color="#64728C">No insurer found</Text>}
          {results.map((p) => (
            <Flex key={p.commonPayerId} justify="space-between" align="center" px="2" py="1" _hover={{ bg: "#F1F2F6" }}>
              <Text fontSize="13px">{p.name}</Text>
              <Button size="xs" variant="ghost" color="#8E4997" onClick={() => { onPick(p); setResults(null); setQ(""); }} data-testid="iv-payer-pick">Choose</Button>
            </Flex>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── insurer names (carrier name → payer) ─────────────────────────────────────

export function InsurerNames() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState({});

  const load = async () => {
    const r = await ivApi.setupPayers();
    if (isOk(r)) setData(r.data);
    else setError(r?.msg || "Could not load the insurers");
  };
  useEffect(() => { load(); }, []);

  const confirm = async (carrierName, payer) => {
    const r = await ivApi.putPayerMap([{ carrier_name: carrierName, commonPayerId: payer.commonPayerId }]);
    if (isOk(r)) {
      setSaved((s) => ({ ...s, [carrierName]: payer.name }));
      load();
    } else setError(r?.msg || "Could not save");
  };

  const mapped = (data?.payers || []).flatMap((p) => (p.carrier_names || []).map((n) => ({ carrier_name: n, payer: p })));
  return (
    <SectionCard title="Insurer names" subtitle="Each insurer name from your PMS, matched to an insurer once." testId="iv-insurer-names">
      {!data && !error && <Spinner size="sm" />}
      {error && <Text fontSize="13px" color="#B3261E">{error}</Text>}
      {(data?.unmapped || []).map((u) => (
        <Box key={u.carrier_name || u.carrier_payer_id} py="2" borderBottom="1px solid #E5E5EF" data-testid="iv-unmapped-row">
          <Flex justify="space-between" align="center" gap="2" wrap="wrap">
            <Text fontSize="13px" fontWeight="600">{u.carrier_name || `Payer ID ${u.carrier_payer_id}`}</Text>
            {saved[u.carrier_name] ? <Badge colorScheme="green">{saved[u.carrier_name]}</Badge> : <Badge colorScheme="orange">Needs a match</Badge>}
          </Flex>
          <Flex gap="2" wrap="wrap" my="1">
            {(u.suggestions || []).map((s) => (
              <Button key={s.commonPayerId} size="xs" variant="outline" onClick={() => confirm(u.carrier_name, s)} data-testid="iv-suggestion">
                {s.name}
              </Button>
            ))}
          </Flex>
          <PayerSearch onPick={(p) => confirm(u.carrier_name, p)} />
        </Box>
      ))}
      {mapped.map((m) => (
        <Flex key={m.carrier_name} justify="space-between" align="center" py="2" borderBottom="1px solid #E5E5EF" gap="2" wrap="wrap" data-testid="iv-mapped-row">
          <Text fontSize="13px">{m.carrier_name}</Text>
          <Text fontSize="13px" fontWeight="600">{m.payer.name}</Text>
          <Box w={{ base: "100%", md: "280px" }}><PayerSearch onPick={(p) => confirm(m.carrier_name, p)} buttonLabel="Change" /></Box>
        </Flex>
      ))}
    </SectionCard>
  );
}

// ── insurance portals ────────────────────────────────────────────────────────

export function PortalConnect({ portal, onChanged }) {
  const [registration, setRegistration] = useState(portal.registration || null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  const register = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await ivApi.createRegistration(portal.portalId);
      if (isOk(r)) setRegistration(r.data);
      else setError(r?.msg || "Could not start the connection");
    } finally {
      setBusy(false);
    }
  };

  const saveLogin = async () => {
    setBusy(true);
    setError("");
    setResult("");
    try {
      const r = await ivApi.putCredentials(portal.portalId, { username, password });
      // the password is never kept or shown again, whatever the outcome
      setPassword("");
      if (!isOk(r)) {
        setError(r?.msg || "The login was not saved");
        return;
      }
      setUsername("");
      const v = await ivApi.validatePortal(portal.portalId);
      const validation = isOk(v) ? v.data?.validation : "";
      setResult(validation === "valid" ? "The login works." : validation === "invalid" ? `The insurer refused the login${v.data?.detail ? `: ${v.data.detail}` : ""}.`
        : validation === "pending" ? "Saved. The insurer is checking the login." : "Saved. The insurer has not confirmed the login yet.");
      onChanged && onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box mt="2" p="3" bg="#F9F9FB" borderRadius="8px" data-testid="iv-portal-connect">
      {!registration ? (
        <Button size="sm" variant="outline" onClick={register} isLoading={busy} data-testid="iv-portal-register">Start connecting</Button>
      ) : (
        <Box mb="2" fontSize="13px" data-testid="iv-portal-registration">
          <Text>{"Use these for the insurer's login codes:"}</Text>
          <Text>Email <b>{registration.email || "—"}</b> · Phone <b>{registration.phone || "—"}</b></Text>
          {portal.url && <Link href={portal.url} isExternal color="#8E4997" fontWeight="600">{"Open the insurer's site"}</Link>}
        </Box>
      )}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2} mt="2" alignItems="end">
        <FormControl>
          <FormLabel fontSize="12px" mb="1">Username</FormLabel>
          <Input size="sm" borderRadius="8px" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" data-testid="iv-portal-username" />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="12px" mb="1">Password</FormLabel>
          <Input size="sm" borderRadius="8px" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password" placeholder={portal.state !== "not_connected" ? "Enter a new password" : ""} data-testid="iv-portal-password" />
        </FormControl>
        <Button size="sm" bg="#154043" color="#F9F9FB" _hover={{ bg: "#123536" }} onClick={saveLogin} isLoading={busy}
          isDisabled={!username || !password} data-testid="iv-portal-save">Save login</Button>
      </SimpleGrid>
      {result && <Text fontSize="13px" mt="2" data-testid="iv-portal-result">{result}</Text>}
      {error && <Text fontSize="13px" mt="2" color="#B3261E">{error}</Text>}
    </Box>
  );
}

export function PortalRow({ portal, onChanged, share }) {
  const [open, setOpen] = useState(false);
  const st = PORTAL_STATES[portal.state] || PORTAL_STATES.not_connected;
  return (
    <Box py="2" borderBottom="1px solid #E5E5EF" data-testid="iv-portal-row">
      <Flex justify="space-between" align="center" gap="2" wrap="wrap">
        <Box>
          <Text fontSize="13px" fontWeight="600">{(portal.payers || []).map((p) => p.name).join(", ") || `Portal ${portal.portalId}`}</Text>
          {share !== undefined && <Text fontSize="12px" color="#64728C">Unlocks the full breakdown for {share}% of your patients</Text>}
          {portal.validation_detail && portal.state === "needs_attention" && <Text fontSize="12px" color="#8A5A00">{portal.validation_detail}</Text>}
        </Box>
        <Flex align="center" gap="2">
          <Badge colorScheme={st.color} data-testid="iv-portal-state">{st.label}</Badge>
          <Button size="xs" variant="outline" onClick={() => setOpen((x) => !x)} data-testid="iv-portal-open">
            {portal.state === "not_connected" ? "Connect" : "Update login"}
          </Button>
        </Flex>
      </Flex>
      {open && <PortalConnect portal={portal} onChanged={onChanged} />}
    </Box>
  );
}

export function InsurancePortals() {
  const [portals, setPortals] = useState(null);
  const [error, setError] = useState("");
  const load = async () => {
    const r = await ivApi.getPortals();
    if (isOk(r)) setPortals(r.data || []);
    else setError(r?.msg || "Could not load the portals");
  };
  useEffect(() => { load(); }, []);
  return (
    <SectionCard title="Insurance portals" subtitle="For the full breakdown. Most insurers need no login." testId="iv-portals">
      {!portals && !error && <Spinner size="sm" />}
      {error && <Text fontSize="13px" color="#B3261E">{error}</Text>}
      {portals && portals.length === 0 && <Text fontSize="13px" color="#64728C">None of your insurers needs a portal login.</Text>}
      {(portals || []).map((p) => <PortalRow key={p.portalId} portal={p} onChanged={load} />)}
    </SectionCard>
  );
}

// ── PDF to the PMS ───────────────────────────────────────────────────────────

export function PdfToPms({ settings, onSaved, saveLabel }) {
  const pdf = settings?.pdf || {};
  const [enabled, setEnabled] = useState(!!pdf.enabled);
  const [category, setCategory] = useState(pdf.category || "");
  const [variant, setVariant] = useState(settings?.pms_pdf_variant || "answers");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const hasQuestions = (settings?.questions || []).some((q) => q && q.enabled !== false);

  if (!pdf.supported) {
    return (
      <SectionCard title="Save PDF to the PMS" testId="iv-pdf">
        <Text fontSize="13px" color="#64728C" data-testid="iv-pdf-unsupported">{"Your PMS doesn't accept files from Viva; results stay in Viva."}</Text>
      </SectionCard>
    );
  }
  const save = async () => {
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const r = await ivApi.putSettings({ pdf: { enabled, category }, pms_pdf_variant: variant });
      if (isOk(r)) {
        setMessage("Saved");
        onSaved && onSaved(r.data);
      } else setError(r?.msg || "Could not save");
    } finally {
      setSaving(false);
    }
  };
  return (
    <SectionCard title="Save PDF to the PMS" subtitle="A PDF of every result is saved to the patient's chart." testId="iv-pdf">
      <FormControl display="flex" alignItems="center" mb="3">
        <Switch id="iv-pdf-enabled" isChecked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="iv-pdf-enabled" />
        <FormLabel htmlFor="iv-pdf-enabled" mb="0" ml="2" fontSize="13px">Save the PDF to the PMS</FormLabel>
      </FormControl>
      <FormControl maxW="320px" mb="3">
        <FormLabel fontSize="13px" mb="1">Document category</FormLabel>
        <Select size="sm" borderRadius="8px" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Choose a category" data-testid="iv-pdf-category">
          {(pdf.categories || []).map((c) => <option key={c.pms_id || c._id} value={c.pms_id || c.name}>{c.name}</option>)}
        </Select>
      </FormControl>
      {hasQuestions && (
        <FormControl data-testid="iv-pdf-variant">
          <FormLabel fontSize="13px" mb="1">Which PDF goes to the PMS</FormLabel>
          <RadioGroup value={variant} onChange={setVariant}>
            <Stack direction="row" spacing={4}>
              <Radio value="answers" size="sm">Answers</Radio>
              <Radio value="full" size="sm">Full</Radio>
            </Stack>
          </RadioGroup>
        </FormControl>
      )}
      <SaveRow onSave={save} saving={saving} message={message} error={error} label={saveLabel} />
    </SectionCard>
  );
}

// ── the practice's insurance questions ───────────────────────────────────────

export function InsuranceQuestions({ settings, onSaved, saveLabel }) {
  const [questions, setQuestions] = useState((settings?.questions || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
  const [view, setView] = useState(settings?.results_view || "answers");
  // the API reports "full" while a practice has no questions; only a choice staff make is saved
  const [viewChosen, setViewChosen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const add = (text) => {
    setMessage("");
    if (questions.length >= MAX_QUESTIONS) {
      setError(`You can have at most ${MAX_QUESTIONS} questions.`);
      return;
    }
    const t = String(text || "").trim();
    if (!t) return;
    if (t.length > MAX_QUESTION_CHARS) {
      setError(`A question can be at most ${MAX_QUESTION_CHARS} characters.`);
      return;
    }
    setError("");
    setQuestions((qs) => [...qs, { text: t, enabled: true }]);
    setDraft("");
  };
  const update = (i, patch) => setQuestions((qs) => qs.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const move = (i, d) => setQuestions((qs) => {
    const j = i + d;
    if (j < 0 || j >= qs.length) return qs;
    const next = qs.slice();
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const remove = (i) => setQuestions((qs) => qs.filter((_, j) => j !== i));

  const save = async () => {
    setMessage("");
    const invalid = validateQuestions(questions);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError("");
    setSaving(true);
    try {
      const body = {
        questions: questions.map((q, i) => ({ ...(q.id ? { id: q.id } : {}), text: q.text.trim(), enabled: q.enabled !== false, order: i })),
        ...(viewChosen ? { results_view: view } : {}),
      };
      const r = await ivApi.putSettings(body);
      if (isOk(r)) {
        setQuestions((r.data?.questions || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
        if (r.data?.results_view) setView(r.data.results_view);
        setMessage("Saved");
        onSaved && onSaved(r.data);
      } else setError(r?.msg || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard title="Your insurance questions" subtitle="The questions you ask when you call an insurer. Results open on the answers." testId="iv-questions">
      {questions.map((q, i) => (
        <Flex key={q.id || `new-${i}`} gap="2" align="center" mb="2" data-testid="iv-question-row">
          <Input size="sm" borderRadius="8px" value={q.text} onChange={(e) => update(i, { text: e.target.value })}
            isInvalid={q.text.length > MAX_QUESTION_CHARS} data-testid="iv-question-text" />
          <Switch isChecked={q.enabled !== false} onChange={(e) => update(i, { enabled: e.target.checked })} aria-label="On" data-testid="iv-question-enabled" />
          <IconButton size="xs" aria-label="Move up" icon={<FaArrowUp />} onClick={() => move(i, -1)} isDisabled={i === 0} data-testid="iv-question-up" />
          <IconButton size="xs" aria-label="Move down" icon={<FaArrowDown />} onClick={() => move(i, 1)} isDisabled={i === questions.length - 1} data-testid="iv-question-down" />
          <IconButton size="xs" aria-label="Remove" icon={<FaTrash />} onClick={() => remove(i)} />
        </Flex>
      ))}
      <Flex gap="2" mt="2">
        <Input size="sm" borderRadius="8px" value={draft} placeholder="Add a question, e.g. What is the frequency for D1110?"
          onChange={(e) => setDraft(e.target.value)} data-testid="iv-question-draft" />
        <Button size="sm" variant="outline" onClick={() => add(draft)} data-testid="iv-question-add">Add</Button>
      </Flex>
      <Flex gap="2" wrap="wrap" mt="2">
        {EXAMPLE_QUESTIONS.filter((x) => !questions.some((q) => q.text === x)).map((x) => (
          <Button key={x} size="xs" variant="ghost" color="#8E4997" onClick={() => add(x)}>+ {x}</Button>
        ))}
      </Flex>
      <Text fontSize="12px" color="#64728C" mt="2">{questions.length} of {MAX_QUESTIONS} questions · up to {MAX_QUESTION_CHARS} characters each</Text>
      <FormControl mt="3">
        <FormLabel fontSize="13px" mb="1">Results open on</FormLabel>
        <RadioGroup value={view} onChange={(x) => { setView(x); setViewChosen(true); }}>
          <Stack direction="row" spacing={4}>
            <Radio value="answers" size="sm">The answers</Radio>
            <Radio value="full" size="sm">The full result</Radio>
          </Stack>
        </RadioGroup>
      </FormControl>
      <SaveRow onSave={save} saving={saving} message={message} error={error} label={saveLabel} />
    </SectionCard>
  );
}

// ── the page ─────────────────────────────────────────────────────────────────

export default function EligibilitySettings() {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [toggling, setToggling] = useState(false);
  // an API that does not say whether this user may change the settings: the portals' refusal says it,
  // and the page waits for it (undefined) — only then
  const [portalsRefused, setPortalsRefused] = useState(false);
  const c = useIvColors();
  const bid = getBid();

  useEffect(() => {
    ivApi.getSettings().then((r) => {
      if (!isOk(r)) {
        setError(r?.msg || "Insurance verification settings are for practice admins.");
        return;
      }
      if (r.data?.can_manage === undefined) {
        setPortalsRefused(undefined);
        ivApi.getPortals().then((p) => setPortalsRefused(isPermissionRefused(p)), () => setPortalsRefused(false));
      }
      setSettings(r.data);
    });
  }, []);
  // every staff member can read the settings, but only those with the business settings permission can
  // change them (SS-4455): the others are told so, rather than shown forms whose every save is refused
  const adminOnly = settings?.can_manage === false || portalsRefused === true;

  const toggle = async (on) => {
    setToggling(true);
    try {
      const r = await ivApi.putSettings({ enabled: on, ...(on ? { setup_zuub: true } : {}) });
      if (isOk(r)) setSettings(r.data);
      else setError([r?.msg, ...(r?.missing || []).map((m) => MISSING_LABELS[m] || m)].filter(Boolean).join(" — "));
    } finally {
      setToggling(false);
    }
  };

  if (adminOnly) {
    return (
      <Box p="6" data-testid="iv-settings-admin-only">
        <Alert status="info" borderRadius="8px"><AlertIcon />
          {"Insurance verification settings are for your practice admins. Ask one of them to make a change."}
        </Alert>
      </Box>
    );
  }
  if (error && !settings) return <Box p="6"><Alert status="warning" borderRadius="8px"><AlertIcon />{error}</Alert></Box>;
  if (!settings || portalsRefused === undefined) return <Flex p="10" justify="center"><Spinner /></Flex>;

  return (
    <Box p={{ base: 3, md: 6 }} maxW="900px" w="100%" overflowY="auto" data-testid="iv-settings">
      <Text fontSize="20px" fontWeight="700" color={c.labelColor}>Insurance verification</Text>
      <Text fontSize="13px" color={c.subColor} mb="3">
        Check eligibility ({settings.credits?.eligibility ?? 2} credits) and get the full breakdown ({settings.credits?.breakdown ?? 8} credits) from the Inbox.
      </Text>
      <Flex align="center" gap="3" mb="5" wrap="wrap">
        <FormControl display="flex" alignItems="center" w="auto">
          <Switch id="iv-enabled" isChecked={!!settings.enabled} onChange={(e) => toggle(e.target.checked)} isDisabled={toggling} data-testid="iv-enabled" />
          <FormLabel htmlFor="iv-enabled" mb="0" ml="2" fontSize="13px">Insurance verification is {settings.enabled ? "on" : "off"}</FormLabel>
        </FormControl>
        <Link href={`${SETUP_PATH}?bid=${bid}`} fontSize="13px" fontWeight="600" color="#8E4997">Open the guided setup</Link>
      </Flex>
      {error && <Alert status="warning" borderRadius="8px" mb="4"><AlertIcon />{error}</Alert>}
      <PracticeDetails settings={settings} onSaved={setSettings} />
      <InsurerNames />
      <InsurancePortals />
      <PdfToPms settings={settings} onSaved={setSettings} />
      <InsuranceQuestions settings={settings} onSaved={setSettings} />
    </Box>
  );
}
